import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { 
    Mic, 
    TrendingUp, 
    TrendingDown,
    Minus,
    Target,
    Trophy,
    AlertCircle,
    Lightbulb,
    Clock,
    MessageSquare,
    Briefcase,
    Shield,
    Headphones,
    ArrowRight,
    Sparkles,
    Loader2,
    ChevronRight,
    BarChart3,
    Volume2,
    RefreshCw
} from 'lucide-react';

const CommunicationProfile = () => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [loadingAiTips, setLoadingAiTips] = useState(false);
    const [aiTips, setAiTips] = useState([]);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await api.get('/communication-score/detailed');
            setData(response.data);
        } catch (error) {
            console.error('Failed to fetch communication data:', error);
            toast.error('Failed to load communication profile');
        } finally {
            setLoading(false);
        }
    };

    const generateAiTips = async () => {
        setLoadingAiTips(true);
        try {
            const response = await api.post('/communication-score/generate-ai-tips');
            setAiTips(response.data.tips || []);
            toast.success('AI coaching tips generated!');
        } catch (error) {
            console.error('Failed to generate AI tips:', error);
            toast.error('Failed to generate tips');
        } finally {
            setLoadingAiTips(false);
        }
    };

    const getScoreColor = (score) => {
        if (score >= 80) return 'text-emerald-500';
        if (score >= 60) return 'text-amber-500';
        return 'text-rose-500';
    };

    const getScoreBgColor = (score) => {
        if (score >= 80) return 'bg-emerald-500/10';
        if (score >= 60) return 'bg-amber-500/10';
        return 'bg-rose-500/10';
    };

    const getRatingBadge = (rating) => {
        const styles = {
            excellent: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
            good: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
            average: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
            needs_work: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
            poor: 'bg-rose-500/10 text-rose-500 border-rose-500/20'
        };
        return styles[rating] || styles.average;
    };

    const getTrendIcon = (direction) => {
        if (direction === 'improving') return <TrendingUp className="h-4 w-4 text-emerald-500" />;
        if (direction === 'declining') return <TrendingDown className="h-4 w-4 text-rose-500" />;
        return <Minus className="h-4 w-4 text-muted-foreground" />;
    };

    const getCategoryIcon = (iconName) => {
        const icons = {
            'mic': Mic,
            'briefcase': Briefcase,
            'shield': Shield,
            'clock': Clock,
            'message-square': MessageSquare,
            'headphones': Headphones,
            'alert-circle': AlertCircle
        };
        const IconComponent = icons[iconName] || Target;
        return <IconComponent className="h-4 w-4" />;
    };

    const getModuleLink = (moduleName) => {
        const links = {
            'role_play': '/roleplay',
            'inside_sales': '/inside-sales',
            'job_simulations': '/job-simulations',
            'sourcing_labs': '/sourcing-labs',
            'evolve': '/evolve',
            'rpat': '/rpat'
        };
        return links[moduleName] || '/dashboard';
    };

    const getModuleDisplayName = (moduleName) => {
        const names = {
            'role_play': 'Role Play Lab',
            'inside_sales': 'Inside Sales Training',
            'job_simulations': 'Job Simulations',
            'sourcing_labs': 'Sourcing Labs',
            'evolve': 'Evolve (Presentations)',
            'rpat': 'R-PAT Training'
        };
        return names[moduleName] || moduleName;
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </MainLayout>
        );
    }

    if (!data) {
        return (
            <MainLayout>
                <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
                    <Mic className="h-16 w-16 text-muted-foreground" />
                    <h2 className="text-xl font-semibold">No Communication Data Yet</h2>
                    <p className="text-muted-foreground text-center max-w-md">
                        Complete voice-based training sessions to build your communication profile.
                    </p>
                    <Link to="/roleplay">
                        <Button>
                            Start Role Play Training
                            <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                    </Link>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="communication-profile-page">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-3">
                            <Volume2 className="h-8 w-8 text-primary" />
                            Communication Profile
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Your comprehensive communication analysis across all training modules
                        </p>
                    </div>
                    <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Refresh
                    </Button>
                </div>

                {/* Overall Score Hero */}
                <Card className="bg-gradient-to-br from-primary/5 via-background to-secondary/5 border-primary/20">
                    <CardContent className="py-8">
                        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                            <div className="flex items-center gap-6">
                                {/* Score Circle */}
                                <div className="relative">
                                    <svg className="w-32 h-32 transform -rotate-90">
                                        <circle
                                            cx="64"
                                            cy="64"
                                            r="56"
                                            stroke="currentColor"
                                            strokeWidth="8"
                                            fill="none"
                                            className="text-muted/20"
                                        />
                                        <circle
                                            cx="64"
                                            cy="64"
                                            r="56"
                                            stroke="currentColor"
                                            strokeWidth="8"
                                            fill="none"
                                            strokeDasharray={`${(data.overall_score / 100) * 352} 352`}
                                            className={getScoreColor(data.overall_score)}
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                        <span className={`text-3xl font-bold ${getScoreColor(data.overall_score)}`}>
                                            {data.overall_score}
                                        </span>
                                        <span className="text-xs text-muted-foreground">/ 100</span>
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Badge className={`${getRatingBadge(data.overall_rating)} capitalize`}>
                                            {data.overall_rating?.replace('_', ' ')}
                                        </Badge>
                                        <div className="flex items-center gap-1">
                                            {getTrendIcon(data.trends?.improvement_direction)}
                                            <span className="text-sm text-muted-foreground capitalize">
                                                {data.trends?.improvement_direction || 'stable'}
                                            </span>
                                        </div>
                                    </div>
                                    <h2 className="text-2xl font-bold">Overall Communication Score</h2>
                                    <p className="text-muted-foreground mt-1">
                                        Based on {data.stats?.total_voice_sessions || 0} voice sessions
                                    </p>
                                </div>
                            </div>

                            {/* Quick Stats */}
                            <div className="grid grid-cols-3 gap-4 text-center">
                                <div className="p-4 rounded-xl bg-muted/50">
                                    <p className="text-2xl font-bold text-primary">{data.stats?.total_voice_sessions || 0}</p>
                                    <p className="text-xs text-muted-foreground">Sessions</p>
                                </div>
                                <div className="p-4 rounded-xl bg-muted/50">
                                    <p className="text-2xl font-bold text-primary">
                                        {(data.stats?.total_words_spoken || 0).toLocaleString()}
                                    </p>
                                    <p className="text-xs text-muted-foreground">Words Spoken</p>
                                </div>
                                <div className="p-4 rounded-xl bg-muted/50">
                                    <p className="text-2xl font-bold text-primary">{data.stats?.practice_time_minutes || 0}m</p>
                                    <p className="text-xs text-muted-foreground">Practice Time</p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="grid w-full grid-cols-4">
                        <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                        <TabsTrigger value="details" data-testid="tab-details">Detailed Analysis</TabsTrigger>
                        <TabsTrigger value="modules" data-testid="tab-modules">By Module</TabsTrigger>
                        <TabsTrigger value="coaching" data-testid="tab-coaching">Coaching Tips</TabsTrigger>
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview" className="space-y-6 mt-6">
                        {/* Sub-Scores Grid */}
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {Object.entries(data.sub_scores || {}).map(([key, score]) => (
                                <Card key={key} className="hover:shadow-md transition-shadow">
                                    <CardContent className="pt-6">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className={`p-2 rounded-lg ${getScoreBgColor(score.score)}`}>
                                                {getCategoryIcon(key === 'voice_clarity' ? 'mic' : 
                                                    key === 'professional_tone' ? 'briefcase' :
                                                    key === 'confidence' ? 'shield' :
                                                    key === 'pace' ? 'clock' :
                                                    key === 'filler_words' ? 'message-square' : 'headphones')}
                                            </div>
                                            <Badge className={`${getRatingBadge(score.rating)} capitalize text-xs`}>
                                                {score.rating?.replace('_', ' ')}
                                            </Badge>
                                        </div>
                                        <h3 className="font-semibold mb-1">{score.label}</h3>
                                        <div className="flex items-end gap-2 mb-3">
                                            <span className={`text-3xl font-bold ${getScoreColor(score.score)}`}>
                                                {score.score}
                                            </span>
                                            <span className="text-muted-foreground text-sm mb-1">/ 100</span>
                                        </div>
                                        <Progress value={score.score} className="h-2" />
                                        {score.wpm && (
                                            <p className="text-xs text-muted-foreground mt-2">
                                                Average pace: {score.wpm} WPM
                                            </p>
                                        )}
                                        {score.density !== undefined && (
                                            <p className="text-xs text-muted-foreground mt-2">
                                                Filler density: {score.density}%
                                            </p>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </div>

                        {/* Strengths & Improvements */}
                        <div className="grid gap-6 md:grid-cols-2">
                            {/* Strengths */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-emerald-500">
                                        <Trophy className="h-5 w-5" />
                                        Your Strengths
                                    </CardTitle>
                                    <CardDescription>What you&apos;re doing well</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {data.strengths?.length > 0 ? (
                                        data.strengths.map((strength, i) => (
                                            <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/10">
                                                <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-500">
                                                    {getCategoryIcon(strength.icon)}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-sm">{strength.category}</p>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {strength.description}
                                                    </p>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-sm text-muted-foreground text-center py-4">
                                            Complete more sessions to identify your strengths
                                        </p>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Areas for Improvement */}
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-amber-500">
                                        <Target className="h-5 w-5" />
                                        Areas to Improve
                                    </CardTitle>
                                    <CardDescription>Focus areas for growth</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {data.improvements?.length > 0 ? (
                                        data.improvements.map((improvement, i) => (
                                            <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/10">
                                                <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-500">
                                                    {getCategoryIcon(improvement.icon)}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="font-medium text-sm">{improvement.category}</p>
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        {improvement.description}
                                                    </p>
                                                    {improvement.action && (
                                                        <p className="text-xs text-primary mt-2 flex items-center gap-1">
                                                            <Lightbulb className="h-3 w-3" />
                                                            {improvement.action}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-sm text-muted-foreground text-center py-4">
                                            Great job! No significant areas identified for improvement
                                        </p>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>

                    {/* Detailed Analysis Tab */}
                    <TabsContent value="details" className="space-y-6 mt-6">
                        {/* Filler Words Analysis */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <MessageSquare className="h-5 w-5 text-primary" />
                                    Filler Words Analysis
                                </CardTitle>
                                <CardDescription>
                                    Specific filler words you use and how to eliminate them
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {data.filler_analysis?.top_fillers?.length > 0 ? (
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
                                            <div className="text-center">
                                                <p className="text-3xl font-bold text-primary">
                                                    {data.filler_analysis.total_count}
                                                </p>
                                                <p className="text-xs text-muted-foreground">Total Fillers</p>
                                            </div>
                                            <Separator orientation="vertical" className="h-12" />
                                            <div>
                                                <p className="text-sm font-medium">
                                                    {data.filler_analysis.needs_attention 
                                                        ? "Your filler word usage needs attention"
                                                        : "Your filler word control is good"}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    Target: Less than 1 filler per 100 words
                                                </p>
                                            </div>
                                        </div>

                                        <div className="grid gap-3">
                                            {data.filler_analysis.top_fillers.map((filler, i) => (
                                                <div 
                                                    key={i} 
                                                    className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <Badge variant="outline" className="font-mono">
                                                            &quot;{filler.word}&quot;
                                                        </Badge>
                                                        <span className="text-sm text-muted-foreground">
                                                            used <span className="font-semibold text-foreground">{filler.count}</span> times
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-primary max-w-xs text-right">
                                                        {filler.tip}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <Trophy className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                                        <p className="font-medium">Excellent filler word control!</p>
                                        <p className="text-sm text-muted-foreground">
                                            You have minimal filler words in your speech
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Trends Chart */}
                        {data.trends?.has_data && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <BarChart3 className="h-5 w-5 text-primary" />
                                        Progress Over Time
                                    </CardTitle>
                                    <CardDescription>
                                        Your communication score trends over the past weeks
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        {/* Simple bar chart visualization */}
                                        <div className="flex items-end gap-2 h-40">
                                            {data.trends.overall_scores?.map((score, i) => (
                                                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                                    <div 
                                                        className={`w-full rounded-t ${getScoreBgColor(score)} transition-all`}
                                                        style={{ height: `${score}%` }}
                                                    />
                                                    <span className="text-xs text-muted-foreground">
                                                        {data.trends.weeks?.[i]}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="flex items-center justify-center gap-2 pt-2 border-t">
                                            {getTrendIcon(data.trends.improvement_direction)}
                                            <span className="text-sm">
                                                {data.trends.improvement_direction === 'improving' 
                                                    ? "Your scores are improving! Keep it up!"
                                                    : data.trends.improvement_direction === 'declining'
                                                    ? "Your scores have dipped. Time for more practice!"
                                                    : "Your scores are stable. Push for the next level!"}
                                            </span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* By Module Tab */}
                    <TabsContent value="modules" className="space-y-6 mt-6">
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {Object.entries(data.module_breakdown || {}).map(([moduleName, moduleData]) => (
                                <Card key={moduleName} className="hover:shadow-md transition-shadow">
                                    <CardContent className="pt-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="font-semibold">{getModuleDisplayName(moduleName)}</h3>
                                            <Badge variant={moduleData.has_data ? "default" : "outline"}>
                                                {moduleData.sessions || 0} sessions
                                            </Badge>
                                        </div>

                                        {moduleData.has_data ? (
                                            <div className="space-y-3">
                                                {moduleData.communication_score !== undefined && (
                                                    <div>
                                                        <div className="flex justify-between text-sm mb-1">
                                                            <span>Communication</span>
                                                            <span className={getScoreColor(moduleData.communication_score)}>
                                                                {moduleData.communication_score}%
                                                            </span>
                                                        </div>
                                                        <Progress value={moduleData.communication_score} className="h-1.5" />
                                                    </div>
                                                )}
                                                {moduleData.voice_score !== undefined && (
                                                    <div>
                                                        <div className="flex justify-between text-sm mb-1">
                                                            <span>Voice</span>
                                                            <span className={getScoreColor(moduleData.voice_score)}>
                                                                {moduleData.voice_score}%
                                                            </span>
                                                        </div>
                                                        <Progress value={moduleData.voice_score} className="h-1.5" />
                                                    </div>
                                                )}
                                                {moduleData.professionalism_score !== undefined && (
                                                    <div>
                                                        <div className="flex justify-between text-sm mb-1">
                                                            <span>Professionalism</span>
                                                            <span className={getScoreColor(moduleData.professionalism_score)}>
                                                                {moduleData.professionalism_score}%
                                                            </span>
                                                        </div>
                                                        <Progress value={moduleData.professionalism_score} className="h-1.5" />
                                                    </div>
                                                )}
                                                {moduleData.overall_score !== undefined && (
                                                    <div>
                                                        <div className="flex justify-between text-sm mb-1">
                                                            <span>Overall</span>
                                                            <span className={getScoreColor(moduleData.overall_score)}>
                                                                {moduleData.overall_score}%
                                                            </span>
                                                        </div>
                                                        <Progress value={moduleData.overall_score} className="h-1.5" />
                                                    </div>
                                                )}
                                                {moduleData.avg_score !== undefined && (
                                                    <div>
                                                        <div className="flex justify-between text-sm mb-1">
                                                            <span>Average Score</span>
                                                            <span className={getScoreColor(moduleData.avg_score)}>
                                                                {moduleData.avg_score}%
                                                            </span>
                                                        </div>
                                                        <Progress value={moduleData.avg_score} className="h-1.5" />
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-muted-foreground text-center py-4">
                                                No sessions completed yet
                                            </p>
                                        )}

                                        <Link to={getModuleLink(moduleName)}>
                                            <Button variant="outline" className="w-full mt-4" size="sm">
                                                {moduleData.has_data ? 'Practice More' : 'Start Training'}
                                                <ChevronRight className="h-4 w-4 ml-1" />
                                            </Button>
                                        </Link>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>

                    {/* Coaching Tips Tab */}
                    <TabsContent value="coaching" className="space-y-6 mt-6">
                        {/* Pre-defined Tips */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Lightbulb className="h-5 w-5 text-amber-500" />
                                    Quick Tips Based on Your Scores
                                </CardTitle>
                                <CardDescription>
                                    Actionable advice based on your current performance
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {data.predefined_tips?.map((tipGroup, i) => (
                                        <div key={i} className="p-4 rounded-lg border bg-card">
                                            <div className="flex items-center justify-between mb-3">
                                                <h4 className="font-medium">{tipGroup.category}</h4>
                                                <Badge 
                                                    className={
                                                        tipGroup.level === 'low' ? 'bg-rose-500/10 text-rose-500' :
                                                        tipGroup.level === 'medium' ? 'bg-amber-500/10 text-amber-500' :
                                                        'bg-emerald-500/10 text-emerald-500'
                                                    }
                                                >
                                                    {tipGroup.score}%
                                                </Badge>
                                            </div>
                                            <ul className="space-y-2">
                                                {tipGroup.tips.map((tip, j) => (
                                                    <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                                                        <ChevronRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                                        {tip}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* AI-Generated Tips */}
                        <Card className="bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-purple-500/20">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Sparkles className="h-5 w-5 text-purple-500" />
                                    AI-Powered Coaching Tips
                                </CardTitle>
                                <CardDescription>
                                    Personalized advice generated by AI based on your communication profile
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {aiTips.length > 0 ? (
                                    <div className="space-y-3">
                                        {aiTips.map((tip, i) => (
                                            <div 
                                                key={i} 
                                                className="p-4 rounded-lg bg-purple-500/5 border border-purple-500/10"
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className="p-1.5 rounded-lg bg-purple-500/10 text-purple-500">
                                                        <Sparkles className="h-4 w-4" />
                                                    </div>
                                                    <p className="text-sm">{tip}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-6">
                                        <Sparkles className="h-12 w-12 text-purple-500/50 mx-auto mb-4" />
                                        <p className="text-muted-foreground mb-4">
                                            Get personalized coaching tips based on your specific communication patterns
                                        </p>
                                        <Button 
                                            onClick={generateAiTips} 
                                            disabled={loadingAiTips}
                                            className="bg-purple-600 hover:bg-purple-700"
                                            data-testid="generate-ai-tips-btn"
                                        >
                                            {loadingAiTips ? (
                                                <>
                                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                    Generating...
                                                </>
                                            ) : (
                                                <>
                                                    <Sparkles className="h-4 w-4 mr-2" />
                                                    Generate AI Tips
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </MainLayout>
    );
};

export default CommunicationProfile;
